import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { authApi } from "./lib/auth.api";
import api from "./lib/axios";
import LoginPage from "./pages/auth/LoginPage";
import BootstrapPage from "./pages/BootstrapPage";
import Layout from "./components/layouts/Layout";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import PartyDetailPage from "./pages/party/PartyDetailPage";
import PartiesPage from "./pages/PartiesPage";
import ProformasPage from "./pages/ProformasPage";
import ProformaDetailPage from "./pages/documents/proforma/components/ProformaDetailPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceDetailPage from "./pages/documents/invoices/components/InvoiceDetailPage";
import CreancesPage from "./pages/CreancesPage";
import CreateProformaPage from "./pages/documents/proforma/components/CreateProformaPage";
import CreateInvoiceDraftPage from "./pages/documents/invoices/components/CreateInvoiceDraftPage";
import { PageHeaderProvider, usePageHeader } from "./components/layouts/lib/page-header";
import StockArticlesPage from "./pages/StockArticlesPage";
import CommissionsPage from "./pages/CommissionsPage";
import CommissionEditQueuePage from "./pages/CommissionEditQueuePage";
import StockEditQueuePage from "./pages/StockEditQueuePage";
import CompanyCommissionsPage from "./pages/CompanyCommissionsPage";
import DashboardPage from "./pages/DashboardPage";
import EmployeeActivityDetailPage from "./pages/EmployeeActivityDetailPage";
import AnalysePage from "./pages/AnalysePage";
import AuditLogPage from "./pages/AuditLogPage";
import MailOutboxPage from "./pages/MailOutboxPage";
import NotificationsPage from "./pages/NotificationsPage";
import AidePage from "./pages/AidePage";
import ProfilePage from "./pages/ProfilePage";
import TrashPage from "./pages/TrashPage";
import CguPage from "./pages/legal/CguPage";
import MentionsLegalesPage from "./pages/legal/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "./pages/legal/PolitiqueConfidentialitePage";

interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  role: "agent" | "manager" | "admin" | "super_admin" | "consultant" | "dev";
}

interface AuthContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  loading: true,
});

export function useAuth() {
  return useContext(AuthContext);
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-neutral-500 text-sm">Chargement...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

// Reserved for Sprint 2+ admin pages (Settings, Users, Catalog) — not yet wired.
export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || !["admin", "super_admin"].includes(user.role)) {
    return <Navigate to={homeFor(user?.role)} replace />;
  }
  return <>{children}</>;
}

function homeFor(role?: string): string {
  if (role === "agent") return "/profile";
  // dev's default surface is ops/monitoring (audit log, mail outbox,
  // backups) — never the business dashboard (C2, 2026-09-23 plan).
  if (role === "dev") return "/audit-log";
  return "/dashboard";
}

// Agent accounts are employee self-service only (their own profile, plus
// attendance features to come) — no access to operational/business data.
// dev is the same shape of restriction, for the same reason: it's a
// maintenance/monitoring role, not an operational one.
export function AgentRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role === "agent" || user?.role === "dev") {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return <>{children}</>;
}

// Parties/Proformas/Stock aren't in the consultant role's 5 allowed modules
// (Dashboard, Analyse, Factures, Créances, Commissions) — block agent,
// consultant, and dev from these, on top of AgentRoute's block.
export function RestrictedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role === "agent" || user?.role === "consultant" || user?.role === "dev") {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return <>{children}</>;
}

export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || user.role !== "super_admin") {
    return <Navigate to={homeFor(user?.role)} replace />;
  }
  return <>{children}</>;
}

// Ops/monitoring surfaces (Settings, Journal d'audit, Historique emails) —
// dev role's default surface (C2), alongside admin+ who already had it.
export function OpsMonitoringRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || !["dev", "admin", "super_admin"].includes(user.role)) {
    return <Navigate to={homeFor(user?.role)} replace />;
  }
  return <>{children}</>;
}

function DefaultRoute() {
  const { user } = useAuth();
  return <Navigate to={homeFor(user?.role)} replace />;
}

function BootstrapRoute({ bootstrapNeeded }: { bootstrapNeeded: boolean }) {
  if (!bootstrapNeeded) return <Navigate to="/login" replace />;
  return <BootstrapPage />;
}

// Placeholder until the real dashboard (M12) lands.
export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapNeeded, setBootstrapNeeded] = useState(false);

  useEffect(() => {
    async function checkSession() {
      try {
        const bootstrapRes = await api.get("/bootstrap/status");
        if (!bootstrapRes.data.initialized) {
          setBootstrapNeeded(true);
          setLoading(false);
          return;
        }

        const response = await authApi.me();
        setUser(response.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    checkSession();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <img
            src="/brand/logo.jpg"
            alt="PrestiX"
            className="w-12 h-12 rounded-full mx-auto object-contain"
          />
          <p className="text-neutral-500 text-sm">Chargement de PrestiX...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, loading }}>
      <PageHeaderProvider>
        <Routes>
          <Route path="/bootstrap" element={<BootstrapRoute bootstrapNeeded={bootstrapNeeded} />} />
          {bootstrapNeeded && <Route path="*" element={<Navigate to="/bootstrap" replace />} />}

          <Route path="/login" element={<LoginPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />

          <Route
            element={
              <ProtectedRoute>
                <Layout userRole={user?.role} userFullName={user?.fullName} />
              </ProtectedRoute>
            }
          >
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/aide" element={<AidePage />} />
            <Route path="/cgu" element={<CguPage />} />
            <Route
              path="/dashboard"
              element={
                <AgentRoute>
                  <DashboardPage />
                </AgentRoute>
              }
            />
            <Route
              path="/reporting/employees/:agentId"
              element={
                <AgentRoute>
                  <EmployeeActivityDetailPage />
                </AgentRoute>
              }
            />
            <Route
              path="/analyse"
              element={
                <AgentRoute>
                  <AnalysePage />
                </AgentRoute>
              }
            />
            <Route
              path="/parties"
              element={
                <RestrictedRoute>
                  <PartiesPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/parties/:id"
              element={
                <RestrictedRoute>
                  <PartyDetailPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/proformas"
              element={
                <RestrictedRoute>
                  <ProformasPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/proformas/new"
              element={
                <RestrictedRoute>
                  <CreateProformaPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/proformas/:id"
              element={
                <RestrictedRoute>
                  <ProformaDetailPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <AgentRoute>
                  <InvoicesPage />
                </AgentRoute>
              }
            />
            <Route
              path="/invoices/new"
              element={
                <RestrictedRoute>
                  <CreateInvoiceDraftPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/invoices/:id"
              element={
                <AgentRoute>
                  <InvoiceDetailPage />
                </AgentRoute>
              }
            />
            <Route
              path="/creances"
              element={
                <AgentRoute>
                  <CreancesPage />
                </AgentRoute>
              }
            />
            <Route
              path="/stock"
              element={
                <RestrictedRoute>
                  <StockArticlesPage />
                </RestrictedRoute>
              }
            />
            <Route
              path="/stock/edit-requests"
              element={
                <AdminRoute>
                  <StockEditQueuePage />
                </AdminRoute>
              }
            />
            <Route
              path="/commissions"
              element={
                <AgentRoute>
                  <CommissionsPage />
                </AgentRoute>
              }
            />
            <Route
              path="/commissions/edit-requests"
              element={
                <AdminRoute>
                  <CommissionEditQueuePage />
                </AdminRoute>
              }
            />
            <Route
              path="/company-commissions"
              element={
                <AdminRoute>
                  <CompanyCommissionsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <OpsMonitoringRoute>
                  <SettingsPage />
                </OpsMonitoringRoute>
              }
            />
            <Route
              path="/trash"
              element={
                <SuperAdminRoute>
                  <TrashPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="/audit-log"
              element={
                <OpsMonitoringRoute>
                  <AuditLogPage />
                </OpsMonitoringRoute>
              }
            />
            <Route
              path="/mail-outbox"
              element={
                <OpsMonitoringRoute>
                  <MailOutboxPage />
                </OpsMonitoringRoute>
              }
            />
          </Route>

          <Route path="/" element={<DefaultRoute />} />
          <Route path="*" element={<DefaultRoute />} />
        </Routes>
      </PageHeaderProvider>
    </AuthContext.Provider>
  );
}
